// Super Admin — Email template preview centre
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Monitor, Smartphone } from "lucide-react";

export default function EmailPreviewPanel() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [width, setWidth] = useState("desktop");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/platform/emails/templates").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load templates")); }, []);
  const open = async (key) => {
    setSel(key); setPreview(null);
    try { const { data } = await api.get(`/platform/emails/preview/${key}`); setPreview(data); }
    catch { toast.error("Preview failed"); }
  };
  const sendTest = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/platform/emails/test/${sel}`); toast.success(`Test sent to ${data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };
  const cats = [...new Set(items.map((i) => i.category))];

  return (
    <div data-testid="email-preview-panel">
      <h2 className="font-display font-black uppercase tracking-tight text-xl">Email templates</h2>
      <p className="text-xs text-zinc-500 mt-0.5">Preview every Fieldlink email with sample data, then send a test to yourself.</p>
      <div className="grid lg:grid-cols-[300px_1fr] gap-5 mt-5">
        <div className="space-y-4">
          {cats.map((c) => (
            <div key={c}>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-zinc-400">{c}</div>
              <div className="mt-1.5 space-y-1">
                {items.filter((i) => i.category === c).map((i) => (
                  <button key={i.key} onClick={() => open(i.key)} data-testid={`email-tpl-${i.key}`}
                    className={`w-full text-left border rounded-lg px-3 py-2 text-xs font-bold flex items-center justify-between ${sel === i.key ? "border-[#FF5F15] bg-orange-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                    {i.name}
                    <span className={`text-[8.5px] font-black uppercase px-1.5 py-0.5 rounded ${i.brand === "customer" ? "bg-blue-100 text-blue-700" : "bg-zinc-100 text-zinc-500"}`}>{i.brand === "customer" ? "Client-facing" : "Fieldlink"}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div>
          {!preview ? (
            <div className="border border-dashed border-zinc-300 rounded-xl h-96 flex items-center justify-center text-sm text-zinc-400">
              {sel ? <Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /> : "Select a template to preview"}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs"><span className="text-zinc-400 font-bold uppercase text-[9.5px]">Subject:</span> <b>{preview.subject}</b></div>
                <div className="flex gap-2">
                  <button onClick={() => setWidth("desktop")} className={`h-8 px-3 border rounded-md text-[10.5px] font-black uppercase inline-flex items-center gap-1 ${width === "desktop" ? "border-[#FF5F15] text-[#FF5F15]" : "border-zinc-300"}`} data-testid="email-width-desktop"><Monitor className="w-3.5 h-3.5" /> Desktop</button>
                  <button onClick={() => setWidth("mobile")} className={`h-8 px-3 border rounded-md text-[10.5px] font-black uppercase inline-flex items-center gap-1 ${width === "mobile" ? "border-[#FF5F15] text-[#FF5F15]" : "border-zinc-300"}`} data-testid="email-width-mobile"><Smartphone className="w-3.5 h-3.5" /> Mobile</button>
                  <button onClick={sendTest} disabled={busy} className="h-8 px-4 bg-[#FF5F15] text-white rounded-md text-[10.5px] font-black uppercase inline-flex items-center gap-1.5" data-testid="email-send-test">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test to me
                  </button>
                </div>
              </div>
              <div className="mt-3 border border-zinc-200 rounded-xl overflow-hidden bg-zinc-100 flex justify-center">
                <iframe title="Email preview" srcDoc={preview.html} data-testid="email-preview-frame"
                  style={{ width: width === "mobile" ? 390 : "100%", height: 760, border: 0, background: "#EDEDEF" }} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
